import { observer } from 'mobx-react-lite'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from 'components/Spinner'
import { useTranslation } from 'react-i18next'
import {
  BreakoutAsyncButton,
  BreakoutButton,
} from 'components/design-system/BreakoutButton'
import { type StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { NewRoomStateCubit } from '@breakoutlearning/firebase-repository/cubits/NewRoomStateCubit'
import { type RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { LockOn } from 'components/icons/LockOn'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { Search } from 'components/icons/Search'
import { useCubitBuilder } from 'hooks/cubits'
import classNames from 'classnames'
import type { TupleUnion } from 'types/TupleUnion'
import { type PublicUser } from '@breakoutlearning/firebase-repository/models/PublicUser'
import { RoomStateFormV2DialogBody } from './RoomStateFormDialogV2'
import {
  ScheduledDayOfMonthOnYear,
  ScheduledDayOfWeekAtTime,
} from '../ScheduledTimeParts'
import { useTranslationTyped } from 'i18n/i18n'
import { RoomCard } from 'components/RoomCard'
import { DateTime } from 'luxon'
import { SlideType } from '@breakoutlearning/firebase-repository/types'

export const NewRoomStateV2 = observer(function NewRoomState({
  cubit,
  headingId,
  closeSelf,
}: {
  cubit: StudentAssignmentCubit
  headingId: string
  closeSelf: () => void
}) {
  const { t, tt } = useTranslationTyped()

  const dialogCubit = useCubitBuilder(
    () => new NewRoomStateCubit(cubit.repository),
    [cubit.repository]
  )

  // The search input mounts when loading ends and again when the user comes
  // back from the join/create view, so focus it whenever it mounts. A stable
  // callback ref runs once per mount, not on every render.
  const focusSearchInputOnMount = useCallback(
    (element: HTMLInputElement | null) => element?.focus(),
    []
  )

  const {
    roomStates: { isLoading, models },
    slides: { models: slides },
  } = cubit

  const processingSlideIndex = slides.findIndex(
    (s) => s.data.slideType === SlideType.processingData
  )

  const { unlockedRooms, lockedRooms } = useMemo(() => {
    const { lockedRooms, unlockedRooms } = dialogCubit.filterAndSortRooms({
      roomsWithStatus: models.map((room) => {
        const roomReachedProcessingSlide =
          processingSlideIndex > 0 &&
          typeof room.data.activeSlide === 'number' &&
          room.data.activeSlide >= processingSlideIndex

        // Include the current user (the join candidate) alongside the room's
        // members: a student with their own extension can still join a group
        // of non-extended members after the base deadline, so the room must
        // not read as expired/locked to them.
        const joinCandidateIds = cubit.repository.uid
          ? [...room.userIds, cubit.repository.uid]
          : room.userIds
        return {
          room,
          status: room.getRoomStateStatus(
            cubit.assignment.getEffectiveExpiresAtForUsers(joinCandidateIds),
            cubit.section.data.sectionState,
            cubit.slideDeck.data.slideDeckSlideCount,
            cubit.assignment.data.assignmentState,
            roomReachedProcessingSlide,
            cubit.assignment.hasFutureIndividualDeadlineForAnyUser(
              joinCandidateIds
            )
          ),
        }
      }),
      assignment: cubit.assignment,
    })
    return {
      lockedRooms,
      unlockedRooms,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dialogCubit,
    dialogCubit.dayFilter,
    dialogCubit.timeFilter,
    dialogCubit.searchString,
    cubit,
    cubit.assignment.data.assignmentState,
    cubit.assignment.expiresAt,
    cubit.assignment.individualDeadlines,
    cubit.section.data.sectionState,
    cubit.slideDeck.data.slideDeckSlideCount,
    models,
  ])

  const { roomToJoin, isPhantomRoom, virtualName } = useMemo(() => {
    const roomWithName =
      dialogCubit.roomIdToJoin === null
        ? undefined
        : unlockedRooms.find(({ room }) => {
            if (room.id !== dialogCubit.roomIdToJoin) return false
            return true
          })

    return {
      roomToJoin: roomWithName?.room,
      isPhantomRoom: roomWithName?.room.isEmpty ?? true,
      virtualName: roomWithName?.virtualName ?? '',
    }
  }, [dialogCubit.roomIdToJoin, unlockedRooms])

  const joinRoom = useCallback(
    async (room: RoomState) => {
      const roomIsEmpty = room.userCount === 0
      await cubit.joinRoom(room.id, { isGroupLeader: roomIsEmpty })
      closeSelf()
    },
    [cubit, closeSelf]
  )

  /**
   * don't display the phantom room if we are filtering for real groups
   * or we've reached the target group number
   * (subtract 1 as the phantom room is included in the unlockedRooms array)
   */
  // NOTE: mbunsch - we're on a tight deadline and I don't wanna break anything
  // so we're gonna hardcode false, and then we can remove the phantom room code
  const showPhantomRoom = false

  // if hiding the phantom room, subtract from filteredLength
  const filteredLength =
    unlockedRooms.length + lockedRooms.length - (showPhantomRoom ? 0 : 1)

  const showNoResults = !isLoading && !filteredLength

  const createDisabled = cubit.assignment.data.forbidStudentGroupCreation
  let createDisabledTooltip = ''
  if (createDisabled) {
    createDisabledTooltip =
      tt.student_assignment.create_group_disabled_by_instructor()
  }

  if (roomToJoin) {
    // if its empty room or phantom (createRoom) we need to show schedule dialog
    const { scheduledAtDate } = roomToJoin
    const { forbidStudentGroupScheduling } = cubit.assignment.data

    const scheduledAtInPast =
      scheduledAtDate && scheduledAtDate < DateTime.now()

    const mustInputScheduledDate =
      !roomToJoin.userCount &&
      (isPhantomRoom ||
        (!forbidStudentGroupScheduling &&
          (!scheduledAtDate || scheduledAtInPast)))

    if (mustInputScheduledDate) {
      return (
        <RoomStateFormV2DialogBody
          room={roomToJoin}
          defaultScheduledAt={!scheduledAtInPast ? scheduledAtDate : undefined}
          cubit={cubit}
          onSave={closeSelf}
          closeSelf={() => dialogCubit.setRoomIdToJoin(null)}
          virtualName={virtualName}
          headingId={headingId}
        />
      )
    }

    return (
      <ConfirmJoinDialogBody
        room={roomToJoin}
        virtualName={virtualName}
        joinRoom={(r) => joinRoom(r)}
        cancel={() => dialogCubit.setRoomIdToJoin(null)}
      />
    )
  }

  return (
    <div className="flex h-full w-full flex-col gap-3 text-core-on-tertiary">
      <div className="mb-3 flex flex-row items-center justify-between">
        <div className="flex w-full flex-row items-center justify-between">
          <h1
            id={headingId}
            className="text-headline-large"
            data-testid="rooms-dialog-heading"
          >
            {createDisabled
              ? tt.student_assignment.join_dialog_v2.join_your_friends()
              : tt.student_assignment.join_dialog_v2.join_your_friends_or_start()}
          </h1>
          <BreakoutButton
            data-testId="start-a-new-group-button"
            kind="secondary"
            size="small"
            tooltip={createDisabledTooltip}
            disabled={!unlockedRooms.length || createDisabled}
            onClick={() => dialogCubit.setRoomIdToJoin('')}
          >
            {tt.student_assignment.join_dialog_v2.start_a_new_group()}
          </BreakoutButton>
        </div>
      </div>

      <section
        aria-label={tt.student_assignment.join_dialog_v2.group_filter()}
        className="flex flex-col gap-3"
      >
        <p className="sr-only">
          {tt.student_assignment.join_dialog_v2.group_filter_description()}
        </p>
        {!isLoading && (
          <div className="flex flex-row gap-5">
            <BreakoutTextInput
              ref={focusSearchInputOnMount}
              name="rooms-filter"
              onChange={(value) =>
                dialogCubit.setSearchString(value.target.value)
              }
              className="grow"
              LeadingIcon={(props) => <Search {...props} />}
              inputClassName="text-body-medium border font-medium placeholder-surface-on-surface-disabled"
              kind="tertiary"
              placeholder={tt.student_assignment.join_dialog_v2.search_groups_and_users()}
              aria-label={tt.student_assignment.join_dialog_v2.search_groups_and_users()}
              iconClassName="text-on-surface-disabled"
            />
            <DayFilterSelector
              currentDay={dialogCubit.dayFilter}
              setDay={(d) => dialogCubit.setDayFilter(d)}
            />
            <TimeOfDaySelector
              currentTimeOfDay={dialogCubit.timeFilter}
              setTimeOfDay={(t) => dialogCubit.setTimeFilter(t)}
            />
          </div>
        )}
        <div aria-live="assertive" className="sr-only">
          {!isLoading &&
            (filteredLength > 0
              ? tt.student_assignment.join_dialog_v2.results_available({
                  count: filteredLength,
                })
              : tt.student_assignment.join_dialog_v2.no_results_available())}
        </div>
        <div className="h-full grow overflow-y-auto">
          {unlockedRooms.length - (showPhantomRoom ? 0 : 1) > 0 && (
            <ul
              className="list-none p-0 pb-2"
              style={{
                display: 'grid',
                gap: '0.5rem',
                gridTemplateColumns: `repeat(auto-fill, minmax(209px, 1fr))`,
              }}
            >
              {unlockedRooms.map(({ room, virtualName, status }) => {
                const isPhantomRoom = room.isEmpty

                if (isPhantomRoom && !showPhantomRoom) return null

                return (
                  <li key={room.id}>
                    <RoomCard
                      context="student"
                      room={room}
                      virtualName={virtualName}
                      roomStateStatus={status}
                      bottomContent={
                        <BreakoutButton
                          data-testid="rooms-dialog-join-group-button"
                          size="small"
                          kind="accent"
                          className="mt-auto"
                          fullWidth
                          onClick={() => dialogCubit.setRoomIdToJoin(room.id)}
                        >
                          {t('student_assignment.join_group')}
                        </BreakoutButton>
                      }
                    />
                  </li>
                )
              })}
            </ul>
          )}

          {lockedRooms.length > 0 && (
            <>
              <h2
                className="my-1 text-title-large"
                data-testid="rooms-dialog-locked-groups-heading"
              >
                {t('student_assignment.join_dialog_v2.locked_groups')}
              </h2>
              <p className="mb-2 text-body-medium">
                {t(
                  'student_assignment.join_dialog_v2.locked_groups_description'
                )}
              </p>
              <ul
                className="list-none p-0"
                style={{
                  display: 'grid',
                  gap: '0.5rem',
                  gridTemplateColumns: `repeat(auto-fill, minmax(209px, 1fr))`,
                }}
              >
                {lockedRooms.map(({ room, virtualName, status }) => (
                  <li key={room.id}>
                    <RoomCard
                      context="student"
                      room={room}
                      roomStateStatus={status}
                      virtualName={virtualName}
                      bottomContent={
                        <BreakoutButton
                          data-testid="rooms-dialog-locked-group-button"
                          size="small"
                          kind="tertiary"
                          className="mt-auto cursor-not-allowed text-core-error!"
                          fullWidth
                          icon={<LockOn size={12} />}
                          tooltip={t(
                            'student_assignment.join_dialog_v2.group_locked_tooltip'
                          )}
                        >
                          {t('student_assignment.locked')}
                        </BreakoutButton>
                      }
                    />
                  </li>
                ))}
              </ul>
            </>
          )}

          {isLoading && (
            <div className="flex h-full w-full items-center justify-center">
              <Spinner />
            </div>
          )}
          {showNoResults && <NoResults />}
        </div>
      </section>
    </div>
  )
})

// const NoRooms = observer(function NoRooms() {
//   const { t } = useTranslation()
//   return (
//     <div className="flex h-full w-full flex-col items-center justify-center gap-1">
//       <PeopleGroup size={30} className="stroke-fixed-accent-color" />
//       <h2 className="text-title-large text-on-surface">
//         {t('student_assignment.join_or_create_group_first_one_here')}
//       </h2>
//       <p className="text-center text-on-surface-var">
//         <MultiLineText
//           string={t(
//             'student_assignment.join_or_create_group_first_one_here_description'
//           )}
//         />
//       </p>
//     </div>
//   )
// })

const NoResults = observer(function NoResults() {
  const { tt } = useTranslationTyped()

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1">
      <Search
        size={50}
        className="stroke-fixed-accent-color"
        aria-hidden="true"
      />
      <h2
        className="text-title-large text-on-surface"
        data-testid="rooms-dialog-no-results"
      >
        {tt.student_assignment.join_dialog_v2.no_groups_found()}
      </h2>
      <p className="text-center text-body-large text-on-surface-var">
        {tt.student_assignment.join_dialog_v2.no_groups_found_description()}
      </p>
    </div>
  )
})

const DayFilterSelector = ({
  currentDay,
  setDay,
}: {
  currentDay: NewRoomStateCubit['dayFilter']
  setDay: NewRoomStateCubit['setDayFilter']
}) => {
  const { t } = useTranslation()
  const days: Record<string, NewRoomStateCubit['dayFilter']> = {
    all: 'all',
    sunday: 'su',
    monday: 'm',
    tuesday: 't',
    wednesday: 'w',
    thursday: 'th',
    friday: 'f',
    saturday: 's',
  } as const

  return (
    <fieldset className="ml-3 flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend className="p-0 text-body-medium font-bold">
        {t('student_assignment.join_dialog_v2.day_of_the_week')}
      </legend>
      <ul className="flex flex-row gap-1">
        {Object.entries(days).map(([day, value]) => (
          <li key={day}>
            <button
              data-testid={`rooms-day-filter-${day}`}
              className={classNames('min-w-6 rounded-lg py-1.5', {
                'text-body-medium': currentDay !== value,
                'bg-fixed-accent-color text-label-medium': currentDay === value,
              })}
              onClick={() => {
                if (day === currentDay) return
                setDay(value)
              }}
              aria-current={currentDay === value ? 'true' : undefined}
            >
              {t(`student_assignment.join_dialog_v2.day_abbreviations.${day}`)}
            </button>
          </li>
        ))}
      </ul>
    </fieldset>
  )
}

const TimeOfDaySelector = ({
  currentTimeOfDay,
  setTimeOfDay,
}: {
  currentTimeOfDay: NewRoomStateCubit['timeFilter']
  setTimeOfDay: NewRoomStateCubit['setTimeFilter']
}) => {
  const { t } = useTranslation()
  const timesOfDay: TupleUnion<NewRoomStateCubit['timeFilter']> = [
    'all',
    'morning',
    'afternoon',
    'evening',
  ]

  return (
    <fieldset className="mr-5 flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend className="p-0 text-body-medium font-bold">
        {t('student_assignment.join_dialog_v2.time_of_day')}
      </legend>
      <ul className="flex flex-row gap-1">
        {timesOfDay.map((timeOfDay) => (
          <li key={timeOfDay}>
            <button
              data-testid={`rooms-time-filter-${timeOfDay}`}
              className={classNames('rounded-lg px-2 py-1.5', {
                'text-body-medium': currentTimeOfDay !== timeOfDay,
                'bg-fixed-accent-color text-label-medium':
                  currentTimeOfDay === timeOfDay,
              })}
              aria-current={currentTimeOfDay === timeOfDay ? 'true' : undefined}
              onClick={() => {
                if (currentTimeOfDay === timeOfDay) return
                setTimeOfDay(timeOfDay)
              }}
            >
              {t(`student_assignment.join_dialog_v2.times_of_day.${timeOfDay}`)}
            </button>
          </li>
        ))}
      </ul>
    </fieldset>
  )
}

const ConfirmJoinDialogBody = observer(function ConfirmJoinDialog({
  room,
  virtualName,
  joinRoom,
  cancel,
}: {
  room: RoomState
  virtualName: string
  joinRoom: (roomId: RoomState) => Promise<void>
  cancel: () => void
}) {
  const { tt } = useTranslationTyped()

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6">
      <h1 className="text-headline-large">
        {room.roomStateName || virtualName}
      </h1>
      <ConfirmJoinDialogUsers users={room.users} />
      <div className="flex w-full max-w-[450px] flex-col gap-3 rounded-2xl border-2 border-fixed-accent-color px-7 py-4">
        <strong className="text-body-medium text-on-surface-var">
          {tt.student_assignment.join_dialog_v2.meeting_time()}
        </strong>
        <div className="flex flex-row justify-between gap-2">
          <strong className="text-title-large">
            <ScheduledDayOfWeekAtTime
              scheduledAt={room.data.scheduledAt}
              tt={tt}
              placeholder={tt.student_assignment.join_dialog_v2.not_scheduled()}
            />
          </strong>
          <strong className="text-title-large">
            <ScheduledDayOfMonthOnYear
              scheduledAt={room.data.scheduledAt}
              placeholder="-"
            />
          </strong>
        </div>
      </div>
      <div className="flex flex-row justify-center gap-3">
        <BreakoutButton
          kind="secondary"
          onClick={cancel}
          className="w-[150px]!"
        >
          {tt.student_assignment.cancel()}
        </BreakoutButton>
        <BreakoutAsyncButton
          data-testid="confirm-join-group-button"
          kind="accent"
          className="w-[150px]!"
          onClick={() => joinRoom(room)}
          loadingText={tt.student_assignment.join_group()}
        >
          {tt.student_assignment.join_group()}
        </BreakoutAsyncButton>
      </div>
    </div>
  )
})

const ConfirmJoinDialogUsers = observer(function ConfirmJoinDialogUsers({
  users,
}: {
  users: RoomState['users']
}) {
  const ref = useRef<HTMLDivElement>(null)

  const [renderAsPureWrap, setRenderAsPureWrap] = useState(false)

  useLayoutEffect(() => {
    if (!ref.current) return
    const { current } = ref
    const observer = new ResizeObserver(() => {
      const { width } = current.getBoundingClientRect()
      if (width < 512) setRenderAsPureWrap(true)
    })
    observer.observe(ref.current)
    return () => observer.unobserve(current)
  }, [])

  // cap at 4 users per row
  // if greater than 4 distribute evenly
  const usersAsRows = useMemo(() => {
    const rowCount = Math.ceil(users.length / 4)
    const rows: RoomState['users'][] = Array.from({ length: rowCount }).map(
      () => []
    )
    users.forEach((user, i) => {
      const row = i % rowCount
      rows[row].push(user)
    })
    return rows
  }, [users])

  if (!users.length) return

  return (
    <div
      className={classNames('flex w-full max-w-[600px]', {
        'flex-wrap justify-center': renderAsPureWrap,
        'flex-col': !renderAsPureWrap,
      })}
      ref={ref}
    >
      {renderAsPureWrap &&
        users.map((user) => <ConfirmJoinUser key={user.id} user={user} />)}
      {!renderAsPureWrap &&
        usersAsRows.map((userRow, i) => (
          <div key={i} className="flex flex-row justify-center">
            {userRow.map((user) => (
              <ConfirmJoinUser key={user.id} user={user} />
            ))}
          </div>
        ))}
    </div>
  )
})

const ConfirmJoinUser = ({ user }: { user: PublicUser }) => {
  return (
    <div className="flex w-[128px] max-w-[128px] flex-col items-center justify-center gap-1.5 px-2 py-1">
      <BreakoutUserAvatar user={user} radius={22.5} />
      <div className="flex w-full max-w-full flex-row items-center justify-center gap-1">
        <strong className="truncate text-body-large">{user.fullName}</strong>
      </div>
    </div>
  )
}
